import React from "react";
import { Box, Container, MenuItem, Select, SelectChangeEvent, Stack, Theme, Typography } from "@mui/material"
import { tSplash } from "locales/splash/cruxTranslation";
import { LOCALE_NAME_MAP, getCurrentLanguage } from "locales/splash/i18n";

interface Props {
  theme: Theme;
}

export default function Footer(props: Props) {
  const theme = props.theme;
  const textColor = theme.palette.primary.contrastText;

  const mobile = screen.width < theme.breakpoints.values.sm

  const locale = getCurrentLanguage();

  // The path name may include a locale already, we need to replace it
  function path_in_locale(path: string, locale: string): string {
    const validLocales = new Set(LOCALE_NAME_MAP.map(([code]) => code));

    // Normalize path to ensure leading slash
    if (!path.startsWith('/')) {
      path = '/' + path;
    }

    const segments = path.split('/');
    const firstSegment = segments[1];

    if (validLocales.has(firstSegment)) {
      // Replace existing locale
      segments[1] = locale;
    } else {
      // Insert locale
      segments.splice(1, 0, locale);
    }

    return segments.join('/');
  }

  function updateLanguage(e: SelectChangeEvent<string>) {
    const newLocale = e.target.value;
    window.location.href = path_in_locale(window.location.pathname, newLocale);
  }

  return (
    <Box
      pt={theme.spacing(3)}
      pb={theme.spacing(3)}
      sx={{ backgroundColor: theme.palette.primary.main }}
    >
      <Container
        style={{
          paddingLeft: theme.spacing(3),
          paddingRight: theme.spacing(3),
        }}
      >
        <Stack direction={mobile ? "column" : "row"} justifyContent="space-around" spacing={2}>
          <Box>
            <Typography color={textColor} variant="h6">
              {tSplash("common.footer.get_in_touch_header")}
            </Typography>
            <Box>
              <Stack spacing={1}>
                <a
                  href="mailto:contact@cruxapp.ca"
                  style={{ textDecoration: "none" }}
                >
                  <Typography variant="body2" color={textColor}>contact@cruxapp.ca</Typography>
                </a>
                <a
                  href="https://www.instagram.com/climbwithcrux"
                  target="_blank"
                  style={{ textDecoration: "none" }}
                >
                  <Typography variant="body2" color={textColor}>Instagram</Typography>
                </a>
                <a
                  href="/discord"
                  target="_blank"
                  style={{ textDecoration: "none" }}
                >
                  <Typography variant="body2" color={textColor}>Discord</Typography>
                </a>
              </Stack>
            </Box>
          </Box>
          <Box>
            <Typography color={textColor} variant="h6">
              {tSplash("common.footer.resources_header")}
            </Typography>
            <Box>
              <Stack spacing={1}>
                <a
                  href="/crux-vs-stokt"
                  style={{ textDecoration: "none" }}
                >
                  <Typography variant="body2" color={textColor}>{tSplash("common.footer.crux_vs_stokt")}</Typography>
                </a>

                <a
                  href="/ios"
                  style={{ textDecoration: "none" }}
                >
                  <Typography variant="body2" color={textColor}>{tSplash("common.footer.download_crux_for_ios")}</Typography>
                </a>

                <a
                  href="/android"
                  style={{ textDecoration: "none" }}
                >
                  <Typography variant="body2" color={textColor}>{tSplash("common.footer.download_crux_for_android")}</Typography>
                </a>

                <a
                  href="https://docs.cruxapp.ca/api-documentation"
                  target="_blank"
                  style={{ textDecoration: "none" }}
                >
                  <Typography variant="body2" color={textColor}>{tSplash("common.footer.api_documentation")}</Typography>
                </a>
              </Stack>
            </Box>
          </Box>
          <Box>
            <Typography color={textColor} variant="h6">
              {tSplash("common.footer.legal_header")}
            </Typography>
            <Box>
              <Stack spacing={1}>
                <a
                  href="/privacy"
                  style={{ textDecoration: "none" }}
                >
                  <Typography variant="body2" color={textColor}>{tSplash("common.footer.privacy_policy")}</Typography>
                </a>

                <a
                  href="/terms_of_service"
                  style={{ textDecoration: "none" }}
                >
                  <Typography variant="body2" color={textColor}>{tSplash("common.footer.terms_of_service")}</Typography>
                </a>
              </Stack>
            </Box>
          </Box>
          <Box>
            <Typography color={textColor} variant="h6">
              {tSplash("common.footer.donate_header")}
            </Typography>
            <Box>
              <Stack spacing={1}>
                <a
                  href="https://www.patreon.com/crux_climbing_app"
                  style={{ textDecoration: "none" }}
                >
                  <Typography variant="body2" color={textColor}>{tSplash("common.footer.patreon")}</Typography>
                </a>
              </Stack>
            </Box>
          </Box>
          <Box>
            <Select
              value={locale}
              onChange={updateLanguage}
              sx={{
                backgroundColor: "white",
                "& .MuiSelect-select": {
                  color: "black"
                }
              }}
              size="small"
              MenuProps={{
                PopoverClasses: {
                  paper: "custom-select-menu"
                },
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "left"
                },
                transformOrigin: {
                  vertical: "bottom",
                  horizontal: "left"
                },

              }}
            >
              {LOCALE_NAME_MAP.map(([locale_code, locale_name]) => (
                <MenuItem key={locale_code} value={locale_code}>
                  {locale_name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Stack>
      </Container>
    </Box>
  )
}
